import useLocalStorage from "use-local-storage";
import { useEffect, useState } from "react";
import NightlightIcon from "@mui/icons-material/Nightlight";
import NightlightOutlinedIcon from "@mui/icons-material/NightlightOutlined";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import {
  Alert,
  AlertColor,
  AlertProps,
  Box,
  createTheme,
  CssBaseline,
  Paper,
  Snackbar,
  Stack,
  Switch,
  ThemeProvider,
  Typography,
} from "@mui/material";

import "./App.css";

import { Login } from "./components/auth";
import { HQ } from "./components/hq";
import {
  CRPS_ORANGE_DARK,
  CRPS_ORANGE_LIGHT,
  fetchAuthLogout,
  fetchAuthValidateToken,
  psLocalStorageCond,
  psLocalStorageFilter,
  psLocalStorageSorted,
  psLocalStorageSortedTotals,
  SNACKBAR_HIDE_MS,
} from "./utils";

const App = () => {
  const [themeLight, setThemeLight] = useLocalStorage(
    "lights-on",
    (localStorage.getItem("lights-on") ?? "true") === "true",
  );

  const [authd, setAuthd] = useState(false);
  const [role, setRole] = useState("");
  const [snackbar, setSnackbar] = useState<Pick<AlertProps, "children" | "severity"> | null>(null);

  const setSnack = (message: string, severity?: AlertColor) =>
    setSnackbar({ children: message, severity: severity ?? "info" });

  const connect = async () => {
    try {
      const { error, username, role: authRole } = await fetchAuthValidateToken();
      if (!error && username) {
        setRole(authRole ?? "");
        setAuthd(true);
      } else {
        throw error;
      }
    } catch (err: any) {
      setAuthd(false);
      const msg = err.toString();
      setSnack(msg, msg === "Logged out" ? "info" : "error");
    }
  };

  const disconnect = () => {
    [
      psLocalStorageFilter("Teachers"),
      psLocalStorageFilter("Classrooms"),
      psLocalStorageFilter("Students"),
      psLocalStorageCond,
      psLocalStorageSorted,
      psLocalStorageSortedTotals,
    ].forEach(i => localStorage.removeItem(i));
    fetchAuthLogout();
    setAuthd(false);
  };

  useEffect(() => {
    connect();
  }, []);

  return (
    <ThemeProvider
      theme={createTheme({
        palette: {
          mode: themeLight ? "light" : "dark",
          background: {
            default: themeLight ? "#f5f5f5" : "#202020",
            paper: themeLight ? "#ffffff" : "#2b2b2b",
          },
          primary: { main: themeLight ? CRPS_ORANGE_LIGHT : CRPS_ORANGE_DARK },
          secondary: { main: themeLight ? "#a7c7e7" : "#6ba3d6" },
        },
      })}
    >
      <CssBaseline />
      <Box sx={{ height: "100%", top: 0, p: 0 }}>
        {authd ? <HQ {...{ disconnect, role, setSnack }} /> : <Login {...{ connect, setSnack }} />}
        {!!snackbar && (
          <Snackbar
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            autoHideDuration={SNACKBAR_HIDE_MS}
            className="no-print"
            onClose={() => setSnackbar(null)}
            open
            sx={{ mb: 1.5 }}
          >
            <Alert {...snackbar} />
          </Snackbar>
        )}
      </Box>
      <footer
        className="no-print"
        style={{
          backgroundColor: themeLight ? "lightgrey" : "#0f0f0f",
          bottom: 0,
          padding: 4,
          position: "sticky",
          zIndex: 99,
        }}
      >
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <Typography variant="body2">{`Copyright © ${new Date().getFullYear()} Classroom PowerSorter, Inc.®`}</Typography>
          <Box>
            <Stack direction="row">
              <Paper
                component={Stack}
                direction="column"
                elevation={0}
                sx={{ backgroundColor: "transparent", justifyContent: "center" }}
              >
                {themeLight ? (
                  <NightlightOutlinedIcon fontSize="small"></NightlightOutlinedIcon>
                ) : (
                  <NightlightIcon fontSize="small"></NightlightIcon>
                )}
              </Paper>
              <Paper
                component={Stack}
                direction="column"
                elevation={0}
                sx={{ backgroundColor: "transparent", justifyContent: "center" }}
              >
                <Switch
                  id="theme-toggle"
                  checked={themeLight}
                  color="default"
                  onChange={() => setThemeLight(v => !v)}
                  size="small"
                />
              </Paper>
              <Paper
                component={Stack}
                direction="column"
                elevation={0}
                sx={{ backgroundColor: "transparent", justifyContent: "center" }}
              >
                {themeLight ? (
                  <WbSunnyIcon fontSize="small"></WbSunnyIcon>
                ) : (
                  <WbSunnyOutlinedIcon fontSize="small"></WbSunnyOutlinedIcon>
                )}
              </Paper>
            </Stack>
          </Box>
        </Stack>
      </footer>
    </ThemeProvider>
  );
};

export default App;
